import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Link as LinkDeRota } from 'react-router-dom';

import { formatarCompetencia } from '../../../shared/dominio/datas';
import { formatarValorEmReais } from '../../../shared/dominio/moeda';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { TrechoDoContrato } from '../../../shared/ui/TrechoDoContrato';
import { ROTULOS_DE_CONFIANCA, ROTULOS_DE_TIPO_DE_REGRA, type TipoDeRegra } from '../revisao/api/tipos';
import {
  GRUPOS_DE_REGRA,
  caminhoDasGlosasDaHipotese,
  opcoesDasRegras,
  opcoesDoResumo,
  origemDaRegra,
  type RegraDoContrato,
} from '../api/mapeamento';
import type { AbaDoContrato } from './abasDoContrato';

function GlosasDaHipotese({ regra }: { regra: RegraDoContrato }): React.JSX.Element | null {
  if (regra.glosas === null) return null;
  if (regra.glosas.quantidade === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Nenhuma glosa importada citou esta hipótese.
      </Typography>
    );
  }
  return (
    <Link component={LinkDeRota} to={caminhoDasGlosasDaHipotese(regra.id)} variant="body2">
      {regra.glosas.quantidade === 1 ? 'Citada em 1 glosa' : `Citada em ${String(regra.glosas.quantidade)} glosas`} ·{' '}
      {formatarValorEmReais(regra.glosas.valorGlosado)} glosados
    </Link>
  );
}

/** Uma regra: o que ela faz numa frase, o trecho literal com página e cláusula, e o que mais importa para quem lê. */
function RegraLida({ regra, mostrarClausula }: { regra: RegraDoContrato; mostrarClausula: boolean }): React.JSX.Element {
  return (
    <Stack spacing={0.5} component="li" sx={{ listStyle: 'none' }} aria-label={`Regra: ${ROTULOS_DE_TIPO_DE_REGRA[regra.tipo]}`}>
      {regra.confianca !== 'alta' ? (
        <Chip
          size="small"
          variant="outlined"
          sx={{ alignSelf: 'flex-start' }}
          label={`Confiança ${ROTULOS_DE_CONFIANCA[regra.confianca].toLowerCase()}`}
        />
      ) : null}
      {regra.oQueFaz !== null ? (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {regra.oQueFaz}
        </Typography>
      ) : null}
      <TrechoDoContrato
        trecho={regra.trecho}
        pagina={regra.pagina}
        rotulo="Trecho do contrato"
        complemento={mostrarClausula ? origemDaRegra(regra) : undefined}
      />
      <GlosasDaHipotese regra={regra} />
    </Stack>
  );
}

const ORDEM_DOS_TIPOS = Object.keys(ROTULOS_DE_TIPO_DE_REGRA) as TipoDeRegra[];

/**
 * Uma lista de regras. Com mais de um tipo, o nome do tipo aparece uma vez, em cima das regras dele, e não
 * repetido em cada regra. Dentro de uma cláusula, a legenda não repete a cláusula (`mostrarClausula`).
 */
export function ListaDeRegras({
  regras,
  mostrarClausula,
  rotulo,
}: {
  regras: readonly RegraDoContrato[];
  mostrarClausula: boolean;
  rotulo?: string;
}): React.JSX.Element {
  const tipos = ORDEM_DOS_TIPOS.filter((tipo) => regras.some((r) => r.tipo === tipo));

  return (
    <Stack spacing={2} role="group" aria-label={rotulo}>
      {tipos.map((tipo) => {
        const doTipo = regras.filter((r) => r.tipo === tipo);
        return (
          <Box key={tipo}>
            {tipos.length > 1 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                {ROTULOS_DE_TIPO_DE_REGRA[tipo]} · {String(doTipo.length)}
              </Typography>
            ) : null}
            <Stack component="ul" spacing={2} sx={{ m: 0, p: 0 }} aria-label={ROTULOS_DE_TIPO_DE_REGRA[tipo]}>
              {doTipo.map((regra) => (
                <RegraLida key={regra.id} regra={regra} mostrarClausula={mostrarClausula} />
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

/**
 * As regras vigentes do contrato (`TP-067`, `D1`/`D5`), agrupadas pelo que dizem: o que está na diária, o
 * que se cobra à parte, o que pede autorização, como se cobra, prazos e glosa. Só regra revisada; a que a
 * IA só propôs aparece como contagem "a revisar", que leva às fontes.
 */
export function RegrasDoContrato({
  contratoId,
  competencia,
  abrir,
}: {
  contratoId: string;
  competencia: string;
  abrir: (aba: AbaDoContrato) => void;
}): React.JSX.Element {
  const consulta = useQuery(opcoesDasRegras(contratoId, competencia));
  const consultaDoResumo = useQuery(opcoesDoResumo(contratoId, competencia));

  if (consulta.isPending) return <EstadoDeCarregamento linhas={4} />;

  if (consulta.isError) {
    return (
      <EstadoDeErro
        erro={consulta.error}
        contexto="Não foi possível carregar as regras do contrato"
        aoReprocessar={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  const regras = consulta.data;
  const aRevisarPorTipo = new Map((consultaDoResumo.data?.regrasPorTipo ?? []).map((r) => [r.tipo, r.aRevisar]));
  const grupos = GRUPOS_DE_REGRA.map((grupo) => ({
    ...grupo,
    regras: regras.filter((r) => grupo.tipos.includes(r.tipo)),
    aRevisar: grupo.tipos.reduce((soma, tipo) => soma + (aRevisarPorTipo.get(tipo) ?? 0), 0),
  }));
  // Grupo sem regra e sem nada a revisar vira um nome numa linha no fim, em vez de um título com "nenhuma".
  // "Não classificável" nem entra nessa linha: regra revisada nunca deveria ficar nele.
  const vazios = grupos.filter((g) => g.regras.length === 0 && g.aRevisar === 0 && g.chave !== 'nao-classificavel');

  return (
    <Stack spacing={3}>
      {grupos.map((grupo) => {
        const doGrupo = grupo.regras;
        const aRevisar = grupo.aRevisar;
        if (doGrupo.length === 0 && aRevisar === 0) return null;

        return (
          <Box component="section" key={grupo.chave} aria-labelledby={`grupo-${grupo.chave}`}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h3" id={`grupo-${grupo.chave}`}>
                {grupo.titulo}
              </Typography>
              <Chip size="small" label={String(doGrupo.length)} aria-label={`${String(doGrupo.length)} regras`} />
              {aRevisar > 0 ? (
                <Button
                  size="small"
                  color="warning"
                  onClick={() => {
                    abrir('fontes');
                  }}
                >
                  {String(aRevisar)} a revisar
                </Button>
              ) : null}
            </Stack>

            {doGrupo.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhuma regra aprovada valendo em {formatarCompetencia(competencia)}.
              </Typography>
            ) : (
              <ListaDeRegras regras={doGrupo} mostrarClausula />
            )}
          </Box>
        );
      })}

      {vazios.length > 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sem regras valendo em {formatarCompetencia(competencia)}: {vazios.map((g) => g.titulo.charAt(0).toLowerCase() + g.titulo.slice(1)).join(' · ')}.
        </Typography>
      ) : null}
    </Stack>
  );
}
